import { useMemo, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";

/**
 * A high-level map of the schema itself — every type the kernel knows, and
 * how they connect — not `RecordGraph`'s one-hop ring around a single
 * record (LO-13x, from Jason's own ask in the 2026-09-23 prep call: "I just
 * want to see a map of the object[s]... at a high level, like all the
 * knowledge that this thing is sitting on top of").
 *
 * ## Why a circle and not `RecordGraph`'s hub-and-spokes
 *
 * `RecordGraph` is a ring around one record because the API stops
 * traversal at one hop and a record can have thousands of neighbours — a
 * hub is the only honest shape for that. This is the opposite case: a
 * handful of *types* (nine built in, plus whatever a governor has approved
 * — nothing here has ever been in the thousands), each with a small,
 * fixed set of declared traversals to the others. A plain circle, edges
 * drawn as declared, is the whole diagram; nothing here is dense enough to
 * need a hub or a force layout, and pretending otherwise would draw
 * structure that is not there.
 *
 * ## What an edge means, and why it is not just "a traversal exists"
 *
 * `/schema` hands back each type's own declared traversals, and the *same*
 * relation is frequently declared from both ends — an Organization's
 * `people` (inbound) and a Person's `organizations` (outbound, if it
 * exists) are one real edge, not two. Drawing both as separate lines would
 * make every two-way relation look twice as connected as a one-way one, so
 * this normalises by `direction` first (the referencing type is always the
 * arrow's tail, regardless of which end declared the traversal that found
 * it) and then merges by the underlying edge names (`relations`), not by
 * path segment — two traversals that name the same edge collapse into one
 * line; two that do not (a Payment `paid_to` an Organization is not the
 * same edge as `paid_by`) stay separate and, where they share a pair of
 * types, are drawn with a small curve so neither hides the other.
 *
 * ## What it does not draw
 *
 * A governor-approved custom type carries no declared traversals yet
 * (`_custom_type_view` in the kernel always returns `relationships: []`) —
 * it appears as an unconnected node, honestly, rather than this component
 * inventing an edge for it or hiding the node until one exists.
 */

const R = 190;
// The marker is a fixed dot — the label sits below it rather than inside
// it. Found live: "Organization" and "Cloud Spend" both overflowed a
// circle sized to hold text, the same failure `RecordGraph` avoids by
// drawing its own neighbour nodes as small dots with the label beside
// them rather than a label-sized pill for every kind of node.
const MARK_R = 7;
const LABEL_PAD = 60;

function splitPascal(name) {
  return String(name || "").replace(/([a-z])([A-Z])/g, "$1 $2");
}

function polar(angle, radius) {
  return [radius * Math.cos(angle), radius * Math.sin(angle)];
}

function buildGraph(types) {
  const list = types || [];
  const n = Math.max(list.length, 1);
  const nodes = list.map((t, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    const [x, y] = polar(angle, R);
    return {
      type: t.type,
      collection: t.collection,
      label: splitPascal(t.type),
      fieldCount: (t.fields || []).length,
      x,
      y,
    };
  });
  const byType = new Map(nodes.map((node) => [node.type, node]));

  // Normalise direction first, then merge by the edge's own identity
  // (`relations`) rather than by path segment — see the module docstring.
  const merged = new Map();
  for (const t of list) {
    for (const rel of t.relationships || []) {
      if (!byType.has(rel.target)) continue; // a target outside this read
      const outbound = rel.direction === "outbound";
      const source = outbound ? t.type : rel.target;
      const dest = outbound ? rel.target : t.type;
      if (source === dest) continue; // a type traversing to itself draws nothing useful here
      const edgeKey = [...(rel.relations || [])].sort().join(",");
      const key = `${source}>${dest}>${edgeKey}`;
      if (!merged.has(key)) {
        merged.set(key, {
          id: key, source, dest, names: new Set(), descriptions: new Set(),
        });
      }
      const e = merged.get(key);
      e.names.add(rel.name);
      if (rel.description) e.descriptions.add(rel.description);
    }
  }

  // Curve offset: the first edge between a given pair of types is
  // straight, every one after it alternates sides and grows — so two
  // distinct relations between the same two types (`paid_to`/`paid_by`)
  // both stay visible rather than tracing the same line.
  const perPair = new Map();
  const edges = [...merged.values()].map((e) => {
    const pairKey = [e.source, e.dest].sort().join("|");
    const index = perPair.get(pairKey) || 0;
    perPair.set(pairKey, index + 1);
    return {
      ...e,
      names: [...e.names],
      descriptions: [...e.descriptions],
      from: byType.get(e.source),
      to: byType.get(e.dest),
      curve: index === 0 ? 0 : Math.ceil(index / 2) * 22 * (index % 2 ? 1 : -1),
    };
  }).filter((e) => e.from && e.to);

  return { nodes, edges };
}

// `minWidth` is the width below which the map scrolls rather than shrinks.
// The console's full-width screen keeps 480; a side panel passes 0 so the map
// scales to the panel instead of pushing it wider.
export default function TypeMap({ types, selected, onSelect, minWidth = 480 }) {
  const theme = useTheme();
  const [focused, setFocused] = useState(null);
  const { nodes, edges } = useMemo(() => buildGraph(types), [types]);

  const viewBox = useMemo(() => {
    const pad = LABEL_PAD;
    return [-R - pad, -R - pad, (R + pad) * 2, (R + pad) * 2].join(" ");
  }, []);

  // `theme.vars`, not `theme.palette` — found live, in dark mode: this
  // theme is CSS-variables-backed (`extendTheme`, `colorSchemeSelector:
  // "data"`), so `theme.palette.*` is the baked-in default scheme's static
  // value and does not follow which scheme is actually active. `sx` resolves
  // this correctly on its own; a raw `fill` attribute, set from JS instead
  // of `sx`, has to ask for the CSS variable explicitly or it renders one
  // scheme's colour in both. Every type name on this map was near-black
  // text.primary on a near-black dark background — invisible, not merely
  // low-contrast — until this was fixed.
  const vars = theme.vars?.palette || theme.palette;
  const palette = {
    line: vars.divider,
    node: vars.text.primary,
    muted: vars.text.secondary,
    selected: vars.primary.main,
    paper: vars.background.paper,
  };

  if (!nodes.length) return null;

  return (
    <Box>
      <Box sx={{ overflowX: "auto", pb: 1 }}>
        <Box
          component="svg"
          viewBox={viewBox}
          role="img"
          aria-label={
            `A map of ${nodes.length} object types and ${edges.length} ` +
            "declared relationships between them. The same relationships " +
            "are listed below this diagram."
          }
          sx={{ width: "100%", minWidth, maxHeight: 460, height: "auto", display: "block" }}
        >
          <defs>
            <marker
              id="type-map-arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M0,0 L10,5 L0,10 z" fill={palette.muted} />
            </marker>
          </defs>

          {edges.map((e) => {
            const dim = focused && focused !== e.from.type && focused !== e.to.type;
            const mx = (e.from.x + e.to.x) / 2;
            const my = (e.from.y + e.to.y) / 2;
            // Perpendicular offset for the curve, so a second edge between
            // the same pair bows away from the first rather than tracing it.
            const dx = e.to.x - e.from.x;
            const dy = e.to.y - e.from.y;
            const len = Math.hypot(dx, dy) || 1;
            const cx = mx + (-dy / len) * e.curve;
            const cy = my + (dx / len) * e.curve;
            // Pull the endpoint back from the node's centre so the
            // arrowhead lands on its edge, not buried under the label.
            const toLen = Math.hypot(e.to.x - cx, e.to.y - cy) || 1;
            const ex = e.to.x - ((e.to.x - cx) / toLen) * (MARK_R + 3);
            const ey = e.to.y - ((e.to.y - cy) / toLen) * (MARK_R + 3);
            return (
              <g key={e.id} opacity={dim ? 0.2 : 1}>
                <path
                  d={`M ${e.from.x},${e.from.y} Q ${cx},${cy} ${ex},${ey}`}
                  fill="none"
                  stroke={palette.line}
                  strokeWidth={1.5}
                  markerEnd="url(#type-map-arrow)"
                />
                <title>
                  {e.from.label} → {e.to.label}: {e.names.join(", ")}
                </title>
              </g>
            );
          })}

          {nodes.map((node) => {
            const isSelected = node.collection === selected;
            const dim = focused && focused !== node.type;
            return (
              <g
                key={node.type}
                opacity={dim ? 0.35 : 1}
                tabIndex={0}
                role="button"
                aria-label={`${node.label}, ${node.fieldCount} field${node.fieldCount === 1 ? "" : "s"}. Show its records.`}
                onMouseEnter={() => setFocused(node.type)}
                onMouseLeave={() => setFocused(null)}
                onFocus={() => setFocused(node.type)}
                onBlur={() => setFocused(null)}
                onClick={() => onSelect?.(node.collection)}
                style={{ cursor: onSelect ? "pointer" : "default", outline: "none" }}
              >
                <title>
                  {node.label} — {node.fieldCount} field{node.fieldCount === 1 ? "" : "s"}
                </title>
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={isSelected ? MARK_R + 2 : MARK_R}
                  fill={isSelected ? palette.selected : palette.paper}
                  stroke={isSelected ? palette.selected : palette.node}
                  strokeWidth={isSelected ? 2.5 : 1.5}
                />
                <text
                  x={node.x}
                  y={node.y + MARK_R + 16}
                  textAnchor="middle"
                  fontSize={12}
                  fontWeight={isSelected ? 700 : 500}
                  fill={palette.node}
                >
                  {node.label}
                </text>
                <text
                  x={node.x}
                  y={node.y + MARK_R + 29}
                  textAnchor="middle"
                  fontSize={9.5}
                  fill={palette.muted}
                >
                  {node.fieldCount} field{node.fieldCount === 1 ? "" : "s"}
                </text>
              </g>
            );
          })}
        </Box>
      </Box>

      {/* The same relationships in words — an SVG is not keyboard-navigable
          on its own and colour is not a signal every reader gets, the same
          argument RecordGraph makes about its own diagram. */}
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {edges.length ? edges.map((e) => (
          <Typography
            key={e.id}
            variant="caption"
            color="text.secondary"
            tabIndex={0}
            onMouseEnter={() => setFocused(e.from.type)}
            onMouseLeave={() => setFocused(null)}
            onFocus={() => setFocused(e.from.type)}
            onBlur={() => setFocused(null)}
          >
            {e.from.label} → {e.to.label} ({e.names.join(", ")})
          </Typography>
        )) : (
          <Typography variant="caption" color="text.secondary">
            No type declares a relationship to another yet.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
